import React, { useLayoutEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useReducedMotion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../../settings/config"
import { useBackClose } from "../../atoms/useBackClose"
import { GlassChip } from "../../notebook/NotebookGallery"
import SchemaDrawing from "./SchemaDrawing"
import { FULL_SCREEN, between, drawnRect, flipFrom, hasSize, insetOf, play } from "./schemaMotion"

// Lo schema a schermo pieno, da guardare: il campo grande quanto lo
// schermo permette, sul fondo del campo fino ai bordi, e nient'altro che la
// ✕. Si apre toccando lo schema nella voce del quaderno e cresce da lì
// (`origin`: dove sta il disegno toccato, `court`, e il fondo intorno a
// lui, `area`); un tocco ovunque, la ✕ o il tasto indietro lo richiudono, e
// il campo torna al suo posto.
//
// Il fondo non compare piano: parte già pieno, ritagliato sul fondo da cui
// si apre, e si allarga fino allo schermo, come nell'editor. Così copre da
// subito il campo fermo lì sotto e se ne vede uno solo, quello che cresce:
// con il fondo in dissolvenza se ne vedevano due, uno sopra l'altro, e alla
// fine uno spariva sull'altro.
//
// In alto, la ✕ e (se c'è, `chip`) l'etichetta della fase sono quelle della
// voce, nello stesso posto e uguali: aprendo e chiudendo non si muovono e
// non si spengono, e così non sfarfallano.

const c = theme.colors

const OPEN = { bg: { clipPath: FULL_SCREEN, opacity: 1 }, court: { x: 0, y: 0, scale: 1 } }

function SchemaFullscreen({ schema, label, chip, origin, onClose }) {
	const reduceMotion = useReducedMotion()
	const parts = { root: useRef(null), bg: useRef(null), court: useRef(null) }
	const motion = useRef(null)
	const leaving = useRef(false)

	// da chiuso: il campo sopra quello toccato, il fondo ritagliato sul suo
	const closedState = () => {
		const court = parts.court.current?.getBoundingClientRect()
		const from = origin?.()
		if (reduceMotion || !hasSize(court) || !hasSize(from?.court)) return null
		return {
			bg: hasSize(from.area) ? { clipPath: insetOf(from.area), opacity: 1 } : { clipPath: FULL_SCREEN, opacity: 0 },
			court: flipFrom(from.court, drawnRect(court, 358, 400)),
		}
	}

	const run = (from, to, enter) => {
		const els = Object.fromEntries(Object.entries(parts).map(([name, ref]) => [name, ref.current]))
		return play(between(els, from, to, { enter }), { duration: enter ? 0.38 : 0.3 })
	}

	useLayoutEffect(() => {
		const closed = closedState()
		if (!closed) return
		motion.current = run(closed, OPEN, true)
		return () => motion.current?.stop()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const close = () => {
		if (leaving.current) return
		const closed = closedState()
		if (!closed) return onClose()
		leaving.current = true
		parts.root.current.style.pointerEvents = "none"
		motion.current?.stop()
		run(OPEN, closed, false).finished.then(onClose)
	}

	useBackClose(close)

	return createPortal(
		<div
			ref={parts.root}
			role="dialog"
			aria-modal="true"
			aria-label={`Schema: ${label}`}
			// in React un tocco risale anche da un portale: fin qui e basta
			onClick={event => {
				event.stopPropagation()
				close()
			}}
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 1200,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "calc(env(safe-area-inset-top) + 3.5rem) 0.5rem calc(env(safe-area-inset-bottom) + 1.5rem)",
				cursor: "zoom-out",
			}}>
			<div
				ref={parts.bg}
				aria-hidden="true"
				style={{ position: "absolute", inset: 0, zIndex: -1, background: c.schema_court }}
			/>
			<div
				ref={parts.court}
				style={{
					// il più grande che ci sta, con la forma del campo
					width: "min(100%, calc((100dvh - 5rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)) * 358 / 400))",
					aspectRatio: "358 / 400",
				}}>
				<SchemaDrawing schema={schema} grass={false} label={`Schema: ${label}`} />
			</div>
			<div
				style={{
					position: "absolute",
					left: "1rem",
					right: "1rem",
					top: "calc(env(safe-area-inset-top) + 1rem)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}>
				{chip ? <GlassChip style={{ height: "2.125rem", fontSize: "0.8125rem" }}>{chip}</GlassChip> : <span />}
				<button
					type="button"
					aria-label="Chiudi lo schema"
					style={{
						width: "2.25rem",
						height: "2.25rem",
						borderRadius: "50%",
						border: 0,
						background: c.background_blur,
						backdropFilter: "blur(8px)",
						WebkitBackdropFilter: "blur(8px)",
						color: c.gunmetal,
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
					}}>
					<FontAwesomeIcon icon={faXmark} />
				</button>
			</div>
		</div>,
		document.body,
	)
}

export default SchemaFullscreen
